import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  FaBroadcastTower,
  FaSyncAlt,
  FaUser,
  FaMoneyBillWave,
  FaCoins,
  FaGift,
  FaClock,
  FaWifi,
  FaArrowLeft,
  FaGlobe,
} from "react-icons/fa";
import { setActivePage, setShowPreloader } from "../store/activePage";
import { AxiosGetMethod } from "../helpers/customAxios";
import HandleLogout from "../hooks/handleLogout";

const getOperatorPhotoUrl = (photo, eventId) => {
  if (!photo) return "";
  if (
    photo.startsWith("data:") ||
    photo.startsWith("http://") ||
    photo.startsWith("https://")
  ) {
    return photo;
  }
  const cleanBase = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  
  if (
    !photo.includes("/app/media/") &&
    !photo.includes("app/media/") &&
    !photo.includes("/media/") &&
    !photo.includes("media/") &&
    eventId
  ) {
    return `${cleanBase}/app/media/events/${eventId}/${photo}`;
  }
  
  const cleanPath = photo.startsWith("/") ? photo : `/${photo}`;
  return `${cleanBase}${cleanPath}`;
};

const LiveTracker = () => {
  const [liveEvents, setLiveEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const dispatch = useDispatch();

  const fetchTrackerData = useCallback(
    async (isManual = false) => {
      if (isManual) setRefreshing(true);
      try {
        const res = await AxiosGetMethod("customer/live-tracker");
        if (res.status === 200) {
          setLiveEvents(res.data.data || []);
          setLastUpdated(new Date());
        } else if (res.status === 401) {
          HandleLogout();
        }
      } catch (err) {
        console.error("Error fetching live tracker data:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Live Tracker",
        activeMenu: "liveTracker",
      }),
    );
    // Initial fetch
    fetchTrackerData();

    // Set polling interval (10 seconds)
    const intervalId = setInterval(() => {
      fetchTrackerData();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [dispatch, fetchTrackerData]);

  const formatTime = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRelativeTime = (timeStr) => {
    if (!timeStr) return "No entries yet";
    const date = new Date(timeStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins === 1) return "1 min ago";
    if (diffMins < 60) return `${diffMins} mins ago`;

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // If there are running events, select the current active event
  const currentEvent = liveEvents[activeTabIdx] || null;

  // Calculate overall event aggregates
  const getEventAggregates = (event) => {
    if (!event || !event.operators) {
      return { cash: 0, online: 0, entries: 0, ornaments: 0, things: 0 };
    }
    return event.operators.reduce(
      (acc, op) => {
        acc.cash += Number(op.total_cash || 0);
        acc.online += Number(op.total_online || 0);
        acc.entries += Number(op.total_entries || 0);
        acc.ornaments += Number(op.total_ornaments || 0);
        acc.things += Number(op.total_things || 0);
        return acc;
      },
      { cash: 0, online: 0, entries: 0, ornaments: 0, things: 0 },
    );
  };

  const aggregates = getEventAggregates(currentEvent);

  return (
    <>
      <style>{`
        @keyframes pulse-glowing {
          0% { transform: scale(0.95); opacity: 0.8; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          70% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
          100% { transform: scale(0.95); opacity: 0.8; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        .live-pulse {
          animation: pulse-glowing 2s infinite;
        }
        @keyframes rotate-sync {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .sync-spin {
          animation: rotate-sync 1s linear infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link
                to="/dashboard/home"
                className="text-slate-500 hover:text-amber-600 transition-colors p-1"
                title="Back to Home"
              >
                <FaArrowLeft size={14} />
              </Link>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Live Operator Tracker</h1>
            </div>
            <p className="text-xs text-slate-500">
              Real-time monitoring of cash collections and digital transactions per operator.
            </p>
          </div>

          <div className="flex items-center gap-3 self-start sm:self-center">
            {lastUpdated && (
              <span className="text-[10px] font-semibold text-slate-400">
                Last checked: {formatTime(lastUpdated)}
              </span>
            )}
            <button
              onClick={() => fetchTrackerData(true)}
              disabled={refreshing}
              className={`flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm transition border border-slate-800 disabled:opacity-50`}
            >
              <FaSyncAlt size={11} className={refreshing ? "sync-spin" : ""} />
              <span>{refreshing ? "Refreshing..." : "Sync Live"}</span>
            </button>
          </div>
        </div>

        {/* Loading overlay */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
            <FaBroadcastTower className="text-slate-400 text-5xl animate-bounce mb-4" />
            <p className="text-sm font-bold text-slate-600">Connecting to Live Feed...</p>
            <p className="text-xs text-slate-400 mt-1">Fetching operator metrics and summaries</p>
          </div>
        ) : liveEvents.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-100">
              <FaBroadcastTower className="text-amber-600 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No Live Events Running Today</h3>
            <p className="text-slate-500 text-xs max-w-md mx-auto leading-relaxed mb-6">
              The Live Tracker activates automatically on the day of your celebration once assigned operators log in and start entering Moi records.
            </p>
            <div className="flex justify-center gap-3">
              <Link
                to="/dashboard/events-list"
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl transition shadow-md shadow-amber-600/10"
              >
                View Registered Events
              </Link>
              <Link
                to="/dashboard/home"
                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        ) : (
          /* Live events active */
          <div className="space-y-8">
            {/* Multi-event tab bar */}
            {liveEvents.length > 1 && (
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl max-w-max border border-slate-200/50">
                {liveEvents.map((ev, idx) => (
                  <button
                    key={ev.event_id}
                    onClick={() => setActiveTabIdx(idx)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                      activeTabIdx === idx
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Event #{ev.event_id} - {ev.event_type}
                  </button>
                ))}
              </div>
            )}

            {/* Selected event details header card */}
            <div className="bg-slate-900 rounded-2xl px-6 py-5 border border-slate-800 text-white flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-xl">
              <div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-500 text-white font-black text-[9px] uppercase tracking-wider live-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                    Live
                  </span>
                  <h2 className="text-lg font-black tracking-tight">{currentEvent.event_type}</h2>
                </div>
                <p className="text-[11px] text-slate-400 font-medium mt-1">
                  ID: #{currentEvent.event_id} &middot; Venue: {currentEvent.event_address}, {currentEvent.event_city}
                </p>
                <p className="text-[10px] text-amber-500/90 font-bold mt-0.5">
                  Organised by: {currentEvent.organisation_name}
                </p>
              </div>

              <div className="flex items-center gap-2 bg-slate-800/80 px-4 py-2 rounded-xl border border-slate-700/60 max-w-max shrink-0">
                <FaWifi className="text-emerald-400 text-xs animate-pulse" />
                <span className="text-[10px] font-bold text-slate-300">
                  {currentEvent.operators ? currentEvent.operators.length : 0} Operator
                  {currentEvent.operators?.length !== 1 ? "s" : ""} active
                </span>
              </div>
            </div>

            {/* Total Aggregates Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Cash Collection */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Cash Collection</span>
                  <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center text-amber-600 border border-amber-100">
                    <FaMoneyBillWave size={12} />
                  </div>
                </div>
                <p className="text-xl font-black text-slate-800 tracking-tight">₹ {aggregates.cash.toLocaleString()}</p>
                <span className="text-[9px] text-slate-400 font-medium">In hand drawer</span>
              </div>

              {/* Online Collection */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Digital Collection</span>
                  <div className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 border border-emerald-100">
                    <FaGlobe size={12} />
                  </div>
                </div>
                <p className="text-xl font-black text-slate-800 tracking-tight">₹ {aggregates.online.toLocaleString()}</p>
                <span className="text-[9px] text-slate-400 font-medium">UPI / Card / NetBank</span>
              </div>

              {/* Total Entries */}
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] col-span-2 lg:col-span-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Guests</span>
                  <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 border border-indigo-100">
                    <FaUser size={12} />
                  </div>
                </div>
                <p className="text-xl font-black text-slate-800 tracking-tight">{aggregates.entries}</p>
                <span className="text-[9px] text-slate-400 font-medium">Moi entries recorded</span>
              </div>
            </div>

            {/* Operator Wise Breakdowns heading */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-3 bg-amber-600 rounded-full" />
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Operator Desk Summary</h3>
              </div>

              {/* Operator cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentEvent.operators && currentEvent.operators.length > 0 ? (
                  currentEvent.operators.map((operator) => {
                    return (
                      <div
                        key={operator.operator_id}
                        className="bg-white rounded-2xl border border-slate-150 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)] overflow-hidden"
                      >
                        {/* Operator Top Profiler Header */}
                        <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 shrink-0 bg-slate-200 flex items-center justify-center">
                            {operator.operator_photo ? (
                              <img
                                src={getOperatorPhotoUrl(operator.operator_photo, currentEvent?.event_id)}
                                alt={operator.operator_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <FaUser className="text-slate-400 text-lg" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-bold text-slate-800 truncate">
                              {operator.operator_name}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-semibold truncate">
                              Mobile: {operator.operator_mobile}
                            </p>
                          </div>
                          <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-[8px] font-black text-amber-700 uppercase">
                            Desk #{operator.operator_id}
                          </span>
                        </div>

                        {/* Card Stats */}
                        <div className="p-5 space-y-4">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                            <div>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                                Moi Cash
                              </p>
                              <p className="text-sm font-black text-slate-800 tracking-tight mt-0.5">
                                ₹ {Number(operator.total_cash).toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                                Moi Digital
                              </p>
                              <p className="text-sm font-black text-slate-800 tracking-tight mt-0.5">
                                ₹ {Number(operator.total_online).toLocaleString()}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                                Total entries
                              </p>
                              <p className="text-sm font-black text-slate-800 tracking-tight mt-0.5">
                                {operator.total_entries}
                              </p>
                            </div>
                          </div>

                          {/* Last entry status footer */}
                          <div className="flex items-center gap-2 pt-3 border-t border-slate-100 text-[10px] font-semibold text-slate-500">
                            <FaClock className="text-slate-400 shrink-0" />
                            <span>Last log:</span>
                            <span className="text-slate-700 font-bold truncate">
                              {getRelativeTime(operator.last_entry_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full py-8 text-center text-xs text-slate-400 font-semibold">
                    No operators are registered or linked to this event.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LiveTracker;
